import React, { useState, useEffect } from 'react';
import {
  Button, Form, Input, Select, Checkbox, Row, Col, InputNumber, Card, notification, Skeleton, Space, Modal,
} from 'antd';
import DefaultLayout from './../../components/layout/DefaultLayout';
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined  } from '@ant-design/icons';
import { arrayMoveImmutable } from 'array-move';
import { DndContext, closestCenter } from '@dnd-kit/core';
import customFieldService from "../../services/customField";


import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams } from "react-router-dom";

const { Option } = Select;
const { TextArea } = Input;

const fieldTypes = ['Input', 'Number', 'Textarea', 'Select', 'Checkbox', 'Radio', 'Date', 'Datetime'];
const fieldBelongsToOptions = ['SCRF', 'CRF', 'Mobile Requisition', 'Master Data Management'];
const gridSizes = ['Small', 'Medium', 'Large'];

function SortableField({ id, field, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={{
        ...style,
        borderRadius: 12,
        background: "#ede5e5", // medium pastel
        border: "1px solid #e3d4d4",
        boxShadow: "0 4px 10px rgba(0,0,0,0.15)", // 👈 darker shadow
        paddingTop: "8px",
      }}
      size="small"
      bodyStyle={{ padding: "12px 16px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            {...listeners}
            style={{
              cursor: "grab",
              fontSize: 18,
              padding: "0 6px",
              userSelect: "none",
              color: "#595959",
            }}
          >
            ⠿
          </span>
          <span {...attributes} style={{ fontWeight: 600, color: "#1f1f1f" }}>
            {field.label || field.name}
          </span>
        </div>
      }
      extra={
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(field)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(field)} />
        </Space>
      }
    >
    <div style={{ fontSize: 13, color: "#434343" }}>
      <b> {field.type}</b> <br />
      <b>Required:</b> {field.required == 1 ? "Yes" : "No"}
    </div>
    </Card>
  );
}
export default function TaskNotificationSetting() {
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [type, setType] = useState('');
  const [defaultDataType, setDefaultDataType] = useState('');
  const { id } = useParams();
  const { formId } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);

  const { confirm } = Modal;

  const getCustomFields = async (page = 1) => {
    setLoading(true);
    await customFieldService.getcustomFields(formId).then((response) => {

      const sorted = [...response].sort((a, b) => a.sort - b.sort);
      setData(sorted);
      setFields(sorted);
      setLoading(false);
    });

    console.log(fields)
  };

  useEffect(() => {
    getCustomFields();
  }, []);

  const handleSubmit = async (values) => {

    const lastSort = fields.length > 0 
      ? Math.max(...fields.map(f => f.sort || 0)) 
      : 0;

    const payload = {
      form: formId, 
      label: values.label || values.name,
      default_value: values.default_value || "",
      sort: editIndex !== null ? (values.sort ?? fields[editIndex].sort) : lastSort + 1, 
      disabled: values.disabled || false,
      restrict_to_admin: values.restrict_to_admin || false,
      required: values.required || false,
      show_on_table: values.show_on_table || false,
      ...values,
    };

    setBtnLoading(true);

    try {
      let response;
      if (editIndex !== null && fields[editIndex]?.id) {
        // UPDATE existing
        response = await customFieldService.updateCustomField(fields[editIndex].id, payload);
        notification.success({
          message: "Custom Field Updated",
        });
      } else {
        // CREATE new
        response = await customFieldService.postCustomFields(payload);
        if (response.success) {
          notification.success({
            message: "Custom Field Added",
            description: response.message,
          });
        }
      }
    } catch (error) {
      if (error.response?.status === 422) {
        notification.error({
          message: "Validation Error",
          description: Object.values(error.response.data.errors).join(", "),
        });
      } else {
        notification.error({
          message: "Error",
          description: error.response?.data?.message || "Something went wrong",
        });
      }
    } finally {
      setBtnLoading(false);
      getCustomFields();  // reload updated list
      form.resetFields();
      setEditIndex(null);
    }
  };
  
  const handleEdit = (item) => {
    const index = fields.findIndex(f => f.name === item.name);
    setEditIndex(index);
    setType(item.type);
    setDefaultDataType(item.defaultDataType || '');
    form.setFieldsValue(item);
  };

  const handleDelete = (item) => {
    confirm({
      title: "Are you sure you want to delete this field?",
      icon: <ExclamationCircleOutlined />,
      content: `Field: ${item.label || item.name}`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await customFieldService.deleteCustomField(item.id); 
          // setFields(fields.filter((f) => f.id !== item.id)); // remove from UI
          getCustomFields();
          notification.success({
            message: 'Field deleted successfully',
          });
          // message.success("Field deleted successfully");
        } catch (err) {
          console.error("Delete failed:", err);
          notification.error({
            message: 'Backend Error',
            description: err.message || err,
          });
          // message.error("Failed to delete field");
        }
      },
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((i) => i.name === active.id);
    const newIndex = fields.findIndex((i) => i.name === over.id);
    const newFields = arrayMoveImmutable(fields, oldIndex, newIndex);

    // assign new sort order
    const updatedFields = newFields.map((field, index) => ({
      ...field,
      sort: index + 1,
    }));

    setFields(updatedFields);

    try {
      const response = await customFieldService.reorderCustomFields(updatedFields);
      notification.success({
        message: 'Sorted Successfully',
      });
    } catch (err) {
      notification.error({
        message: 'Backend Error',
        description: err.message || err,
      });
      console.error("Failed to reorder", err);
    }
  };

  return (
    <DefaultLayout>
      <Row gutter={16}>
        {/* Left column: Form */}
        <Col
          span={12}
          style={{
            maxHeight: 800,
            overflowY: 'auto',
            paddingTop: '50px',  
          }}
        ><h2 style={{ marginBottom: 24 }}>Sort & Manage</h2>
          {/*<DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>*/}
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div
            style={{
            // background: "#e3d4d4",   
            // border: "1px solid #e3d4d4",
            borderRadius: "8px",
            paddingTop: "16px",
            paddingBottom: "16px",
            paddingLeft: "30px",
            paddingRight: "30px",
            // boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
            >
            <SortableContext items={fields.map(f => f.name)} strategy={verticalListSortingStrategy}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {fields.map((field) => (
                  <SortableField
                    key={field.name}
                    id={field.name}
                    field={field}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
            </div>
          </DndContext>
        </Col>

        {/* Right column: Sortable field cards */}
        <Col span={12} 
          style={{padding: '50px'}}
        >
          <h2 style={{ marginBottom: 24 }}>Add New Field</h2>
          <Form form={form} layout="vertical" onFinish={handleSubmit}  
          style={{border:'1px solid #e3d4d4', borderRadius:'10px', padding:'30px', background: '#ede5e5'}}
          initialValues={{
            sort: 0,
            disabled: false,
            restrict_to_admin: false,
            required: false,
            show_on_table: false,
            default_value: "",
            col_desktop: "Small",
            col_mobile: "Small",
          }}>

            <Form.Item label="Type" name="type" rules={[{ required: true }]}>
              <Select onChange={(val) => setType(val)}>
                {fieldTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Field Name" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label="Label" name="label" rules={[{ required: true }]}>
              <Input />
            </Form.Item>


            
            {['Select', 'Multi'].includes(type) && (
              <>
                <Form.Item label="Default Data Type" name="data_type">
                  <Select
                    placeholder="Select default data type"
                    onChange={(val) => setDefaultDataType(val)}
                    allowClear
                  >
                    <Option value="static">Static</Option>
                    <Option value="dynamic">Dynamic</Option>
                  </Select>
                </Form.Item>

                {defaultDataType === 'static' && (
                  <Form.Item
                    label="Static Values (use | to separate)"
                    name="data_values"
                  >
                    <TextArea rows={2} />
                  </Form.Item>
                )}

                {defaultDataType === 'dynamic' && (
                  <Form.Item
                    label="Table Reference"
                    name="data_reference"
                  >
                    <Input placeholder="Enter table name or query ref" />
                  </Form.Item>
                )}
              </>
            )}


            <Form.Item label="Default Value" name="default_value">
              <Input />
            </Form.Item>

            <Form.Item label="Grid Size (Desktop)" name="col_desktop">
              <Select>
                {gridSizes.map(size => <Option key={size} value={size}>{size}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Grid Size (Mobile)" name="col_mobile">
              <Select>
                {gridSizes.map(size => <Option key={size} value={size}>{size}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item name="restrict_to_admin" valuePropName="checked">
                <Checkbox value="adminOnly">Restrict to Admins</Checkbox>
            </Form.Item>
            <Form.Item name="required" valuePropName="checked">
                <Checkbox value="required">Required</Checkbox>
            </Form.Item>
            <Form.Item name="show_on_table" valuePropName="checked">
              <Checkbox>Show on Table</Checkbox>
            </Form.Item>

            <Button loading={btnLoading} type="primary" htmlType="submit" block>
              {editIndex !== null ? 'Update Field' : 'Add Field'}
            </Button>
          </Form>
        </Col>
        
      </Row>
    </DefaultLayout>
  );
}